import { toast } from 'react-toastify';
import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Link } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import "./login.css";
import { ROUTES, MESSAGE } from '../../../constant';
import BaseNavbar from '../../commanComponents/navbar/baseNavbar';
import { saveJsonData, saveData } from "../../../utils/localStorage";
import TextInputField from '../../commanComponents/textInputField/textInputField';
import PrimaryButton from '../../commanComponents/primaryButton/primaryButton';
import TurnstileWidget from '../../commanComponents/turnstileWidget/turnstileWidget';
import { useAuth } from "../../../modules/authContext";


const getSafeReturnTo = () => {
    const returnTo = new URLSearchParams(window.location.search).get("return_to");
    if (!returnTo) return null;

    try {
        const targetUrl = new URL(returnTo);
        const allowedHost = targetUrl.hostname === "localhost" ||
            targetUrl.hostname === "127.0.0.1" ||
            targetUrl.hostname === "fizrobotics.com" ||
            targetUrl.hostname.endsWith(".fizrobotics.com");

        return allowedHost && targetUrl.protocol.startsWith("http") ? targetUrl.toString() : null;
    } catch {
        return null;
    }
}


const Login = () => {
    
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [captchaResetKey, setCaptchaResetKey] = useState(0);
    const { login } = useAuth();
    
    const loginSchema = z.object({
        username: z.string().min(1, { message: MESSAGE.THIS_REQUEIRED_FIELD.replace("{0}", "UserName")}).refine((username) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const idRegex = /^FRL\/[A-Z]{2,6}\/[A-Z]{2,6}\/\d{7,8}$/;

            if (emailRegex.test(username)) {
                return true; // It's a valid email
            }

            if (idRegex.test(username)) {
                return true; // It's a valid special ID
            }

            return false; // Invalid input
        }, MESSAGE.FIELD_INVALID.replace("{0}", "Email")),
        password: z.string().min(1, { message: MESSAGE.THIS_REQUEIRED_FIELD.replace("{0}", "Password") }),
        role: z.string().default("USER")
    });
    const { register, handleSubmit, setError, formState: { errors } } = useForm({
        resolver: zodResolver(loginSchema)
    });


    const handleSignIn = (data) => {
        if (!captchaToken) {
            toast.error("Please complete the verification and try again.");
            return;
        }
        setIsLoading(true);
        (async () => {
            try {
                const apiResponse = await login(data.username, data.password, captchaToken);
                saveJsonData("user", apiResponse.data);
                saveData("access", apiResponse.data.token.access);
                saveData("refresh", apiResponse.data.token.refresh);
                setIsLoading(false);

                const returnTo = getSafeReturnTo();
                if (returnTo) {
                    window.location.href = returnTo;
                    return;
                }

                if (apiResponse.data.role_permission.role_type.indexOf("SCHOOL") > -1) {
                    window.location.pathname = ROUTES.ADMIN_DASHBOARD;
                }
                else {
                    if (apiResponse?.data?.is_profile_created === true){
                        window.location.pathname = ROUTES.DASHBOARD;
                    }
                    else{
                        window.location.pathname = ROUTES.CREATE_STUDENT_PROFILE;
                    }
                }
            } catch (error) {
                setIsLoading(false);
                if (error?.response?.data?.error === "username" || error?.response?.data?.error === "password") {
                    setError(error?.response?.data?.error, {type: "custom", message: error?.response?.data?.message[0]})
                }
                else {
                    toast.error(error?.response?.data?.message?.[0] || error.message, {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            }
            finally {
                setCaptchaToken(null);
                setCaptchaResetKey((key) => key + 1);
            }
        })();
    }

    return (
        <>
            <BaseNavbar activeItem="SIGN_IN" />
            <div className='main-div'>
                <div className='form-div'>
                    <Form onSubmit={handleSubmit(handleSignIn)}>
                        <Form.Label className='heading'>
                            SIGN IN
                        </Form.Label>
                        <Form.Label className='sub-heading'>
                            Welcome, to FIRE Robotics Lab(FRL)!
                            <br/>Let the FIRE begin!
                        </Form.Label>

                        <TextInputField
                            placeholder="Email or FRL ID"
                            fieldType="text"
                            errorMessage={errors.username?.message}
                            inputKey="username"
                            formRegister={register}
                        />
                        <TextInputField
                            placeholder="Password"
                            fieldType="password"
                            errorMessage={errors.password?.message}
                            inputKey="password"
                            formRegister={register}
                        />
                        <div className='login-forgot-div'>
                        <Link to={ROUTES.FORGOT_PASSWORD} className='login-forgot-text'>
                            <u>Forgot Password?</u>
                        </Link>
                        </div>
                        <TurnstileWidget key={captchaResetKey} action="login" onToken={setCaptchaToken} />
                        <br />

                        <div className='submit-button-div'>
                            <PrimaryButton
                                text="SIGN IN"
                                type={"submit"}
                                isLoading={isLoading}
                                onlyDisabled={isLoading || !captchaToken}
                                customStyle={{height: "40px"}}
                            />
                        </div>
                        <div className='create-account-div'>
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
}

export default Login;
